import $ from "jquery";
import ko from "knockout";
import _ from "lodash";
import embeddingService from "services/embeddingService";
import LOG from "services/loggingService";
import navHelper from "services/navHelper";
import authService from "services/authService";
import i18n from "extensions/i18n/knockout.i18n";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
import { getHashConversationId } from "util/uriUtil";

// Constants

const HELP_SYSTEM_URI = "https://help.genesys.cloud";

// Route Service

const routeService = {};

// Previous Route

const previousRoute = ko.observable(null);

routeService.back = function () {
    if (
        previousRoute() &&
        previousRoute() !== "#" &&
        previousRoute() !== "#noop"
    ) {
        navHelper.navigateTo(previousRoute(), undefined, false);
    } else {
        navHelper.navigateTo("#directory:", undefined, false);
    }
};

/**
 * Forces reevaluation of the route.  Sometimes IE does not dispatch hashChange native when a hash changes.  Sammy
 * has an internal polling interval for this - but if a hashchange is dispatched from another component - the poll
 * is removed and hash observation ceases.  This triggers hashChange in a way that allows Sammy to continue to
 * poll - and will trigger evaluation of the route.
 */
routeService.triggerHashChange = function () {
    $(window).trigger("hashchange", [true]);
};

const createInteractionPageBreadcrumbs = function (interactionId) {
    return [
        {
            text: i18n.renderString("siteMap.interactionDetails"),
            active: false,
            path: interactionId,
        },
    ];
};
const helpUrlPages = {
    viewInteractionDetails: "view-interactions-details",
    details: "view-an-interactions-details-tab",
    timeline: "view-interactions-timeline",
    audit: "view-interactions-audit-trail",
    qualitySummary: "view-interactions-evaluation-summary",
    journeys: "view-an-interactions-predictive-engagement-journey-data",
    calibrations: "conduct-calibration",
};

const getContextualHelpUrl = function (route) {
    const locale = i18n.getLocale();
    let routeUrl = helpUrlPages.viewInteractionDetails;

    if (route) {
        const lastSegment = route.substring(route.lastIndexOf("/") + 1);

        if (helpUrlPages[lastSegment]) {
            routeUrl = helpUrlPages[lastSegment];
        }

        // when we create new evaluation/caibration in qualitySummaryTab - we get "/new" as last segment -
        // In order to handle this case , we have added this 'includes' check
        else if (route.includes("qualitySummary")) {
            routeUrl = helpUrlPages.qualitySummary;
        } else if (route.includes("journeys")) {
            routeUrl = helpUrlPages.journeys;
        } else if (route.includes("calibrations")) {
            routeUrl = helpUrlPages.calibrations;
        }
    }
    return routeUrl;
};

// Current Route

routeService.route = ko.observable("#").extend({ notify: "always" });
routeService.route.subscribe(function (route) {
    LOG.info("Route Changed:", route);

    const conversationId = getHashConversationId(
        route,
        featureToggleService.isEnabled(
            FeatureToggle.REC_5171_USE_NEW_ANALYTICS_ROUTE
        )
    );
    const interactionsList = i18n.renderString("siteMap.interactionsList");
    const interactionDetails = i18n.renderString("siteMap.interactionDetails");

    const myInteractionsPath = "#/analytics/agents/interactions/me";
    const interactionSearchPath = "#/analytics/interactions";

    const interactionSearchRequirements = {
        all: [
            "analytics:conversationDetail:view",
            "analytics:conversationAggregate:view",
        ],
        any: [
            "conversation:communication:view",
            "quality:evaluation:add",
            "quality:calibration:view",
            "quality:evaluation:editScore",
        ],
    };

    const myInteractionsRequirements = {
        all: ["analytics:conversationAggregate:view"],
        any: [
            "analytics:conversationDetail:view",
            "analytics:agentConversationDetail:view",
        ],
    };

    const iduiInteractionSearchRequirements = {
        all: interactionSearchRequirements.all.map(
            (permission) => `perm:${permission}`
        ),
        any: interactionSearchRequirements.any.map(
            (permission) => `perm:${permission}`
        ),
    };

    const canViewInteractionSearch = authService.hasAuthorization(
        iduiInteractionSearchRequirements,
        true
    );

    const interactionSearchSegment = {
        text: interactionsList,
        path: interactionSearchPath,
        isHostPath: true,
    };

    const myInteractionsSegment = {
        text: interactionsList,
        path: myInteractionsPath,
        isHostPath: true,
        permissions: myInteractionsRequirements,
    };

    const interactionSegment = {
        text: interactionDetails,
        path: conversationId,
        active: false,
    };

    // only text will be displayed if these permission requirements aren't met
    interactionSearchSegment.permissions = interactionSearchRequirements;

    // if the user cannot access the segment based on permissions, directory will render the breadcrumb as text
    const chosenInteractionsSegment = canViewInteractionSearch
        ? interactionSearchSegment
        : myInteractionsSegment;

    embeddingService.sendPageData({
        breadcrumbs: {
            segments: [chosenInteractionsSegment, interactionSegment],
            enableCopyUrl: true,
        },
    });
    const helpUrl = getContextualHelpUrl(route);
    embeddingService.sendPageData({
        helpPath: helpUrl,
    });
});

// Update previous route on route change

routeService.route.subscribe(
    function (oldRoute) {
        previousRoute(oldRoute);
    },
    null,
    "beforeChange"
);

routeService.pathParameters = ko.observable();
routeService.pageInfo = ko.observable();

routeService.segments = ko.computed(function () {
    if (routeService.route()) {
        return routeService
            .route()
            .replace(/^#\/?/, "")
            .replace(/\/$/, "")
            .split("/");
    }
});

// Routes and URLs for contextual help link.  This is set
// on Sammy's route definition.

const helpRoute = ko.observable("");

routeService.getHelpURLForRoute = function (pageUrl) {
    const locale = i18n.getLocale();

    if (pageUrl) {
        const currentHash = pageUrl,
            hashRegex = /^#/,
            hash = currentHash.replace(hashRegex, "");

        return HELP_SYSTEM_URI + "/redirect/" + locale + "/" + hash;
    } else {
        return HELP_SYSTEM_URI + "/redirect/" + locale + "/";
    }
};

routeService.helpURL = ko.computed({
    read: function () {
        return helpRoute();
    },
    write: function (value) {
        helpRoute(routeService.getHelpURLForRoute(value));
        embeddingService.notifyHelpURL(helpRoute());
    },
});

export default routeService;
